import {
  GetListParams, Identifier, SortPayload,
} from 'react-admin';
import queryString from 'query-string';
import _ from 'lodash';
import type {
  Asset,
  Alarm,
  Customer,
  StaticResponderRelation,
  Zone,
  ContactListRef,
  AssetGroupRef,
  CustomerRef,
  AssetRef,
  PointGeometry, Device, ClientConfigGroupRef,
} from '@x-guard/xgac-types/xgac';
import { SubscriptionCallback } from '@react-admin/ra-realtime';
import * as turf from '@turf/turf';
import {
  ATS_CUSTOMER_LINK_RED_ORANGE_WITH_ALARMCENTER_TEMPLATE_ID,
  XGAC_MAIN_API_URL,
} from '../config';
import { httpClient, typedHttpClient } from '../utils/httpClient';
// eslint-disable-next-line import/no-cycle
import { customerCreateDefaults } from '../utils/customerCreateDefaults';
import {
  RaXgacRecord, RecordNameAsRaXgacRecord,
} from '../lib/RaXgacRecord';
import { TypedDataProvider } from './typedDataProvider';
import { updateComparison } from './updateComparison';
import {
  normalizeObservationTriggerForForm, toXgacCreateDto, toXgacUpdateDto,
} from './XgacDto';
import {
  afterTranslateResource, preTranslateResource,
} from './resourceTranslators';
import { WebSocketService } from './xgacWebSocket';
import { getCurrentCustomer } from '../lib/currentCustomer';
import { isClientConfigGroupVisibleForCustomer } from '../lib/clientConfigHelpers';
import { invalidateClientConfigGroupsFullListQuery } from '../lib/clientConfigReactQuery';
// eslint-disable-next-line import/no-cycle
import { qrCodeDataProvider } from './qrCodeDataProvider';
import authProvider from '../utils/authProvider';
import { calculateLargestReminderPeriodInDays } from '../utils/timeGranularityUtils';

const apiUrl = XGAC_MAIN_API_URL;

const socketService = new WebSocketService();

const getIdentity = async () => {

  try {

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return (await authProvider.getIdentity()).id;

  } catch (error) {

    console.error('Error getting identity', error);
    return null;

  }

};

const parseClientConfigGroupRecord = (record: any) => {

  const definitions = record?.clientConfig?.app?.[0]?.definitions || [];
  return {
    ...record,
    id: record._id,
    customerSpecific: Boolean(record?.customer?._id),
    groupIds: definitions
      .filter((definition: any) => definition?.type === 'group')
      .map((definition: any) => definition.group._id),
    keyDefinitions: definitions
      .filter((definition: any) => definition?.type === 'key')
      .map((definition: any) => ({
        key: definition.key,
        value: definition.value,
        editableBy: definition.editableBy,
      })),
  };

};

/** Full scans are required to merge customer + global groups; cache avoids repeating that work on every list page change. */
const CLIENT_CONFIG_GROUPS_FETCH_PAGE_SIZE = 100;

type ClientConfigGroupsMergedCacheEntry = {
  records: ReturnType<typeof parseClientConfigGroupRecord>[];
};

const clientConfigGroupsMergedListCache = new Map<string, ClientConfigGroupsMergedCacheEntry>();
const clientConfigGroupsMergedListInflight = new Map<string, Promise<ClientConfigGroupsMergedCacheEntry['records']>>();

function invalidateClientConfigGroupsMergedListCache() {

  clientConfigGroupsMergedListCache.clear();
  clientConfigGroupsMergedListInflight.clear();

}

function invalidateClientConfigGroupsListCaches() {

  invalidateClientConfigGroupsMergedListCache();
  invalidateClientConfigGroupsFullListQuery();

}

function clientConfigGroupsMergedListCacheKey(
  customerId: string | null | undefined,
  sort: SortPayload | undefined,
) {

  const customerPart = customerId ?? '__no_customer__';
  const field = sort?.field ?? 'name';
  const order = sort?.order ?? 'ASC';
  return `${customerPart}:${field}:${order}`;

}

async function fetchAllClientConfigGroupsPages(
  paginateGroups: (query?: {
    customer?: string;
    $offset?: number;
    $max?: number;
  }) => Promise<{ data?: { result?: any[]; total?: number } }>,
  customerId?: string,
) {

  const max = CLIENT_CONFIG_GROUPS_FETCH_PAGE_SIZE;
  const scopedQuery = customerId ? { customer: customerId } : {};

  let totalItems = -1;
  const allGroups: any[] = [];
  let listOffset = 0;
  let emptyRetriesRemaining = 2;

  while (totalItems < 0 || allGroups.length < totalItems) {

    const response = await paginateGroups({
      ...scopedQuery,
      $offset: listOffset,
      $max: max,
    });
    const batch = response.data?.result || [];

    if (totalItems < 0) {

      const reportedTotal = response.data?.total;
      totalItems = typeof reportedTotal === 'number' && Number.isFinite(reportedTotal) && reportedTotal >= 0
        ? reportedTotal
        : batch.length;

    }

    if (totalItems === 0) {

      break;

    }

    if (batch.length === 0) {

      if (emptyRetriesRemaining <= 0) {

        break;

      }
      emptyRetriesRemaining--;
      continue;

    }

    emptyRetriesRemaining = 2;
    allGroups.push(...batch);
    listOffset += batch.length;

  }

  return allGroups;

}

async function buildMergedClientConfigGroupsRecords(params: {
  customer: { value: string } | undefined | null;
  paginateGroups: (query?: {
    customer?: string;
    $offset?: number;
    $max?: number;
  }) => Promise<{ data?: { result?: any[] } }>;
  sort: SortPayload | undefined;
}) {

  const { customer, paginateGroups, sort } = params;
  const [customerGroups, allGroupsUnscoped] = await Promise.all([
    customer?.value
      ? fetchAllClientConfigGroupsPages(paginateGroups, customer.value)
      : Promise.resolve<any[]>([]),
    fetchAllClientConfigGroupsPages(paginateGroups),
  ]);
  const globalGroups = allGroupsUnscoped.filter((record) => !record?.customer?._id);
  const mergedRaw = _.uniqBy([...customerGroups, ...globalGroups], '_id')
    .filter((record) => isClientConfigGroupVisibleForCustomer(record, customer?.value));
  const mergedParsed = mergedRaw.map((record) => parseClientConfigGroupRecord(record));
  const field = sort?.field ?? 'name';
  const order = sort?.order ?? 'ASC';
  const sortField = field === 'id' ? 'id' : field;
  if (sortField === 'name') {

    const direction = order === 'DESC' ? -1 : 1;
    return [...mergedParsed].sort((left, right) => direction * left.name.localeCompare(right.name));

  }
  return _.orderBy(mergedParsed, [sortField], [order === 'DESC' ? 'desc' : 'asc']);

}

async function getOrLoadMergedClientConfigGroupsRecords(
  cacheKey: string,
  load: () => Promise<ClientConfigGroupsMergedCacheEntry['records']>,
) {

  const cached = clientConfigGroupsMergedListCache.get(cacheKey);
  if (cached) {

    return cached.records;

  }
  let inflight = clientConfigGroupsMergedListInflight.get(cacheKey);
  if (!inflight) {

    inflight = load()
      .then((records) => {

        clientConfigGroupsMergedListCache.set(cacheKey, { records });
        clientConfigGroupsMergedListInflight.delete(cacheKey);
        return records;

      })
      .catch((error) => {

        clientConfigGroupsMergedListInflight.delete(cacheKey);
        throw error;

      });
    clientConfigGroupsMergedListInflight.set(cacheKey, inflight);

  }
  return inflight;

}

function translateListParams(resource: string, params: GetListParams) {

  if (params.filter && params.filter.assetGroups) {

    params.filter['assetGroups._id'] = params.filter.assetGroups;
    delete params.filter.assetGroups;

  }
  if (params.filter && params.filter.zoneIds) {

    params.filter['zone._id'] = params.filter.zoneIds;
    delete params.filter.zoneIds;

  }
  if (params.filter && params.filter.beaconCode) {

    params.filter.beaconCodes = [`/${params.filter.beaconCode}/`];
    delete params.filter.beaconCode;

  }
  if (resource === 'beacons') {

    params = {
      ...params,
      filter: {
        ...params.filter,
        type: 'default',
      },
    };

  }
  if (resource === 'app-users') {

    params = {
      ...params,
      filter: {
        ...params.filter,
        app: '!null',
      },
    };

  }
  if (resource === 'assets') {

    params = {
      ...params,
      filter: {
        ...params.filter,
        app: 'null',
      },
    };

  }
  if (resource === 'customer-user-relations' && params.meta?.accessRequestReviewList) {

    const filter: Record<string, unknown> = {
      ...(params.filter || {}),
      'accessRequest.validUntil': '!null',
    };
    if (!params.meta?.accessRequestReviewIncludeClosed) {

      filter['accessRequest.review'] = 'null';

    }
    params = {
      ...params,
      filter,
    };

  }
  return params;

}

export async function sendPicture(image: any, entityId: string, entityType = 'assets') {

  const formData = new FormData();
  formData.append('photo_url', image);
  let url = `${apiUrl}/${entityType}/app/photo/?id=${entityId}`;
  if (entityType === 'kiosk-users') {

    url = `${apiUrl}/specials/kiosk-user/photo/?id=${entityId}`;

  }
  if (entityType === 'customers') {

    url = `${apiUrl}/customers/kiosk/photo/?id=${entityId}`;

  }
  const imageRequest = await httpClient(url, { method: 'POST', body: formData });
  return JSON.parse(imageRequest.body).image;

}

function getPointsAroundPosition(steps: number, position: PointGeometry): PointGeometry['coordinates'][] {

  const circle = turf.circle(
    turf.point(position.coordinates),
    0.01 * steps,
    {
      steps: Math.max(steps, 4),
      units: 'kilometers',
    },
  );

  // get the points
  return turf.getCoords(circle)[0].slice(0, steps);

}

export const xgacDataProvider: TypedDataProvider = {

  getList: async (resource, params) => {

    if (resource === 'client-config-groups') {

      const customer = params.meta?.customer || getCurrentCustomer();
      const listCustomerOnlyClientConfigGroups = params.filter?.includeGlobalClientConfigGroups === false;
      const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
      const offset = (page - 1) * perPage;
      const paginateGroups = async (query?: {
        customer?: string;
        $offset?: number;
        $max?: number;
      }) => typedHttpClient().clientConfig.clientConfigControllerPaginateGroups({
        $offset: query?.$offset ?? 0,
        $max: query?.$max ?? perPage,
        ...(query?.customer ? { customer: query.customer } : {}),
      });

      if (listCustomerOnlyClientConfigGroups) {

        const response = await paginateGroups({
          ...(customer?.value ? { customer: customer.value } : {}),
          $offset: offset,
          $max: perPage,
        });
        let records = (response.data?.result || []).map((record) => parseClientConfigGroupRecord(record));
        if (customer?.value) {

          records = records.filter((record) => record.customer?._id === customer.value);

        }
        return {
          data: records,
          total: customer?.value ? records.length : (response.data.total || records.length),
        };

      }

      const mergedCacheKey = clientConfigGroupsMergedListCacheKey(customer?.value, params.sort);
      const mergedRecords = await getOrLoadMergedClientConfigGroupsRecords(
        mergedCacheKey,
        () => buildMergedClientConfigGroupsRecords({
          customer,
          paginateGroups,
          sort: params.sort,
        }),
      );
      return {
        data: mergedRecords.slice(offset, offset + perPage),
        total: mergedRecords.length,
      };

    }

    if (params.pagination?.perPage === -1) {

      return xgacDataProvider.getAll(resource, params);

    }
    params = translateListParams(resource, params);
    if (!params.filter) {

      params.filter = {};

    }
    if (!['users/without-app', 'devices/without-app', 'service-accounts'].includes(resource)) {

      resource = preTranslateResource(resource);

    }

    if (!params.pagination) {

      params.pagination = { page: 1, perPage: 100 };

    }
    if (!params.sort) {

      params.sort = { field: 'createdAt', order: 'DESC' };

    }
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const customer = params.meta?.customer || getCurrentCustomer();
    const omitCustomerFilterForAccessReview = resource === 'customer-user-relations'
      && Boolean(params.meta?.accessRequestReviewList);
    const filter = params.filter;
    const betweenFilter = filter.createdAt_between;
    delete filter.createdAt_between;
    Object.keys(filter).forEach((key) => {

      const splitKey = key.split('_');
      if (splitKey.length > 1 && splitKey[0] === 'in') {

        filter[splitKey[1]] = `/${filter[key]}/`;
        delete filter[key];

      }

    });

    let query = {
      $offset: (page - 1) * perPage,
      $max: perPage,
      $sort: `${resource === 'alarms' ? 'ack.value,' : ''}${((order === 'DESC') ? '-' : '')}${((field === 'id') ? '_id' : field)}`,
      ...(!omitCustomerFilterForAccessReview && !['customers', 'responsePartners', 'service-accounts', 'roles', 'domain-links'].includes(resource))
        ? { 'customer._id': customer.value } : {},
      ...(resource === 'assets') ? { app: 'null' } : {},
      ...(resource === 'responsePartners') ? { 'responsePartner.enabled': true } : {},
      ...(resource === 'service-accounts') ? { isServiceAccount: true } : {},
      ...(resource === 'fingerprints' ? { $select: 'zone,deviceInfo,enabled,description,createdAt,updatedAt,dataCount,user' } : {}),
      ...filter,
    };
    if (params.meta?.$select) {

      query.$select = params.meta.$select;

    }

    if (resource === 'zones' && params.meta?.includeFingerprints) {

      query = {
        ...query,
        includeFingerprints: true,
      };

    }

    if (resource === 'alarms') {

      const contextString = localStorage.getItem('context');

      if (contextString) {

        const context = JSON.parse(contextString);

        const customers = context.customerTree;

        const foundCustomer = _.find(customers, { _id: customer.value });

        if (foundCustomer?.isAlarmCenter) {

          delete query['customer._id'];
          query = { ...query, 'alarmCenter._id': customer.value };

        }

      }

    }

    if (resource === 'problematic-alarms') {

      const result = await httpClient(`${apiUrl}/health/open-alarms/admin`, { method: 'POST' }, {
        secret: '',
        timePassed: 120,
        customerIds: [
          '1fbee26c-bf00-46f7-b149-c512601e248b',
          '62908a4b-0d8d-4787-975b-a854205aa99f',
          '98240644-af9a-4b8c-9952-b14a90748287',
          '500acf48-09ad-4b13-9a50-e8524ab0699e',
          '8eef9740-4a43-4404-b4d0-d53616194a32',
          '6b91bd42-33de-480f-960b-21d6ac0d117c',
          'bd4e5aa9-97f8-4a69-b121-471f34240769',
          '3fbe9370-3b73-424d-a139-625bc6b0899f',
          'aef49985-dabd-430d-aec6-20a454a28048',
          '78712634-f017-401d-9c0f-0d9b622e0ab7',
          'bd4e5aa9-97f8-4a69-b121-471f34240769',
          'b0dbd287-9960-42cc-9736-22df530efe0f',
          '782442ee-454d-44b4-9598-fbb80ebb33d7',
          'b2ab867a-503c-44bd-adef-38b1de1aca8d',
          'ffc82072-f8b0-4aca-a0a8-7de410487e35',
          'f5e3e5d0-1848-4d5b-a7ad-0bb00ab246b5',
          'b1dd3181-a98d-4ec2-9033-b5448923027e',
        ],
      });

      return {
        data: result.json.result.map((document: Alarm) => {

          return RecordNameAsRaXgacRecord(document, 'alarms');

        }),
        total: result.json.total,
      };

    }

    resource = afterTranslateResource(resource);

    let url = `${apiUrl}/${resource}?${queryString.stringify(query)}`;

    if (betweenFilter) {

      url = `${url}&createdAt>${betweenFilter[0]}&createdAt<${betweenFilter[1]}`;

    }

    if (filter.q && filter.q.length > 0 && resource !== 'users/without-app') {

      url = `${apiUrl}/${resource}/search?${queryString.stringify(query)}`;

    }

    if (resource === 'users' && filter.globalRoles?.includes('admin')) {

      url = `${url}&!isServiceAccount`;

    }

    const result = await httpClient(url).then(({ json }) => ({
      data: json.result.map((record: Record<string, unknown>) => (RecordNameAsRaXgacRecord(record, resource))),
      total: json.total,
    }));

    if (resource === 'customers') {

      if (result.data.responsePartners) {

        result.data.responsePartners = result.data.responsePartners.map((partner: Customer) => partner._id);

      }

    }
    if (resource === 'asset-groups') {

      result.data.assets = result.data.assets?.map((asset: Asset) => asset._id);

    }
    if (resource === 'assets') {

      result.data = result.data?.map((asset: any) => {

        asset.assetGroups = asset.assetGroups?.map((assetGroup: AssetGroupRef) => assetGroup._id);

        asset.certificationCategories = _.uniq(
          (asset.certifications ?? [])
            .map((c: { certificationCategory: { _id: string } }) => c.certificationCategory._id),
        );

        return asset;

      });

    }
    if (resource === 'security-inventory-categories') {

      result.data = result.data?.map((category: any) => {

        category.notifiedPeople.peopleToNotify = category.notifiedPeople.peopleToNotify?.map((asset: AssetRef) => asset._id) ?? [];
        category.largestReminderPeriodInDays = calculateLargestReminderPeriodInDays(
          category.reminderPeriods ?? [],
        );
        return category;

      });

    }
    if (resource === 'certification-categories') {

      result.data = result.data?.map((category: any) => {

        category.notifiedPeople.peopleToNotify = category.notifiedPeople.peopleToNotify?.map((asset: AssetRef) => asset._id) ?? [];
        category.largestReminderPeriodInDays = calculateLargestReminderPeriodInDays(category.reminderPeriods ?? []);
        return category;

      });

    }
    if (resource === 'domain-links') {

      result.data = result.data.map((domainLink: any) => {

        domainLink.customers = domainLink.customers.map((customerRef: CustomerRef) => customerRef._id);
        return domainLink;

      });

    }

    if (resource === 'registration-templates') {

      result.data = result.data.map((template: any) => {

        template.profile = {
          ...template.profile,
          assetGroups: template.profile?.assetGroups?.map((assetGroup: AssetGroupRef) => assetGroup._id),
          configGroups: template.profile?.configGroups?.map((configGroup: ClientConfigGroupRef) => configGroup._id),
          assetGroupLimit: template.profile?.assetGroupLimit?.map((assetGroup: AssetGroupRef) => assetGroup._id),
        };

        return template;

      });

    }

    if (resource === 'report-triggers') {

      result.data = result.data.map((reportTrigger: any) => {

        if (reportTrigger.config.type === 'zoneOverview' && reportTrigger.config?.minOccupancy?.assetCount) {

          if (reportTrigger.config.schedule?.entries?.length > 0) {

            reportTrigger.config.schedule.entries.forEach((entry: any) => {

              entry.minOccupancy = entry.minOccupancy || reportTrigger.config.minOccupancy.assetCount;

            });

          } else {

            reportTrigger.config.schedule = {
              entries: [
                {
                  daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                  timeStart: '00:00',
                  timeEnd: '23:59',
                  minOccupancy: reportTrigger.config.minOccupancy.assetCount,
                },
              ],
            };

          }
          delete reportTrigger.config.minOccupancy;

        }
        if (reportTrigger.config?.assetGroups) {

          reportTrigger.config.assetGroups = reportTrigger.config.assetGroups.map((assetGroup: AssetGroupRef) => assetGroup._id);

        }
        return reportTrigger;

      });

    }
    if (resource === 'observation-triggers') {

      result.data = result.data.map(normalizeObservationTriggerForForm);

    }
    if (resource === 'zones') {

      result.data = result.data?.map((zone: any) => {

        if (zone.location?.coordinates?.[0]) {

          const coordinates = zone.location?.coordinates?.[0];

          const coordinatesLength = coordinates.length;
          if (coordinatesLength > 2 && _.isEqual(coordinates[coordinatesLength - 1], coordinates[coordinatesLength - 2])) {

            coordinates.pop();

          }

        }
        return zone;

      });

      if (params.meta?.size) {

        result.data = result.data.filter((zone: Zone) => {

          return turf.area(zone.location) > params.meta.size;

        });

      }

    }

    socketService.setActiveIdsPerResource(resource, result.data.map((record: Record<string, unknown>) => record._id));

    if (resource === 'specials/bhv-knop/device-list') {

      return {
        ...result,
        data: result.data.sort((a: Asset & { device: Device }, b: Asset & { device: Device }) => {

          if (a.device.type === 'loraGateway' && b.device.type === 'lora') {

            return 1;

          } if (a.device.type === 'lora' && b.device.type === 'loraGateway') {

            return -1;

          }
          return 0;

        }),
      };

    }
    return result;

  },

  getOne: async (resource, params) => {

    if (resource === 'client-config-groups') {

      const customer = params.meta?.customer || getCurrentCustomer();
      const response = await typedHttpClient().clientConfig.clientConfigControllerFindGroupById(
        String(params.id),
        customer?.value ? { customer: customer.value } : undefined,
      );
      return { data: parseClientConfigGroupRecord(response.data) };

    }

    if (resource === 'user-certifications') {

      const assetId = params.meta?.assetId;

      const { json } = await httpClient(`${apiUrl}/assets/${assetId}`);
      const certification = (json?.certifications || []).find((c: any) => c._id === params.id);

      if (!certification) {

        throw new Error('User certification not found');

      }

      return { data: { ...certification, id: certification?._id } };

    }

    resource = preTranslateResource(resource);

    const result = await httpClient(`${apiUrl}/${resource}/${params.id}`);
    if (resource === 'assets') {

      result.json.hasApp = (result.json.app !== null);
      result.json.userId = result.json.user?._id || null;
      result.json.properties = {
        ...result.json.properties,
        image: result.json.properties?.image ? { title: 'image', src: result.json.properties.image } : undefined,

      };
      result.json.staticResponders = result.json.staticResponders.map((responder: StaticResponderRelation) => (
        { ...responder, staticResponder: responder.staticResponder._id }
      ));
      result.json.assetGroups = result.json.assetGroups.map((assetGroup: AssetGroupRef) => assetGroup._id);

      result.json.insideZones = result.json.insideZones.map((zone: Zone) => zone._id);

    }
    if (resource === 'users') {

      result.json.properties = {
        ...result.json.properties,
        image: result.json.properties?.image ? { title: 'image', src: result.json.properties.image } : undefined,
      };

    }
    if (resource === 'overlays') {

      result.json.imageUrl = result.json.imageUrl ? { title: 'image', src: result.json.imageUrl } : undefined;

    }

    if (resource === 'asset-groups') {

      result.json.assets = result.json.assets.map((record: Asset) => record._id);

    }
    if (resource === 'security-inventory-categories') {

      result.json.notifiedPeople.peopleToNotify = result.json.notifiedPeople.peopleToNotify?.map((asset: AssetRef) => asset._id) ?? [];
      result.json.largestReminderPeriodInDays = calculateLargestReminderPeriodInDays(result.json.reminderPeriods ?? []);

    }
    if (resource === 'certification-categories') {

      result.json.notifiedPeople.peopleToNotify = result.json.notifiedPeople.peopleToNotify.map((asset: AssetRef) => asset._id) ?? [];
      result.json.largestReminderPeriodInDays = calculateLargestReminderPeriodInDays(result.json.reminderPeriods ?? []);

    }
    if (resource === 'customers') {

      if (result.json.responsePartners) {

        result.json.responsePartners = result.json.responsePartners.map((partner: any) => partner._id);

      }

      result.json.billingContracts = result.json.billingContracts.map((contract: any) => {

        return { name: contract };

      });

      result.json.hasAlarmAgeLimit = !!result.json.preferences?.alarmAgeLimit;

      result.json.useDefaultMergeConfig = !result.json.preferences?.mergeConfig;

      if (result.json.kioskConfig) {

        result.json.hasKioskConfig = true;

      }
      result.json.kioskConfig = result.json.kioskConfig ? {
        ...result.json.kioskConfig,
        schedule: {
          ...result.json.kioskConfig.schedule,
          enabled: result.json.kioskConfig.schedule?.entries?.length > 0,
        },
        exposeAssets: result.json.kioskConfig?.exposeAssets?.map((asset: AssetRef) => asset._id),
        exposeLabels: result.json.kioskConfig?.exposeLabels?.map((asset: AssetRef) => asset._id),
        countLabelSeparately: result.json.kioskConfig?.exposeLabels?.[0]?.countLabel,
        alarm: result.json.kioskConfig?.alarm !== undefined ? {
          ...result.json.kioskConfig.alarm,
          audio: result.json.kioskConfig.alarm.focus?.red?.sound?.enabled || false,
          flash: result.json.kioskConfig.alarm.focus?.red?.flash || false,
          focus: result.json.kioskConfig.alarm.focus ? {
            ...result.json.kioskConfig.alarm.focus.red,
          } : undefined,
        } : undefined,
        homepage: result.json.kioskConfig.homepage ? {
          ...result.json.kioskConfig?.homepage,
          logo: result.json.kioskConfig?.homepage?.logo ? { title: 'image', src: result.json.kioskConfig.homepage.logo } : undefined,
        } : undefined,
      } : undefined;

    }
    if (resource === 'beacons') {

      result.json.codes = result.json.codes.map((code:string) => {

        return { name: code };

      });

    }

    if (resource === 'zones') {

      result.json.staticResponders = result.json.staticResponders.map((responder: StaticResponderRelation) => (
        { ...responder, staticResponder: responder.staticResponder._id }
      ));

      result.json.hasAddressPosition = !!result.json.location?.properties?.address?.formattedAddress;

      if (result.json.location?.coordinates?.[0]) {

        const coordinates = result.json.location?.coordinates?.[0];

        const coordinatesLength = coordinates.length;
        if (_.isEqual(coordinates[coordinatesLength - 1], coordinates[coordinatesLength - 2])) {

          coordinates.pop();

        }

      }

    }
    if (resource === 'specials/kiosk-user') {

      result.json.device.kioskConfig = {
        ...result.json.device.kioskConfig,
        screenSchedule: {
          ...result.json.device.kioskConfig.schedule,
          enabled: result.json.device.kioskConfig.schedule?.entries?.length > 0,
        },
        exposeAssets: result.json.device.kioskConfig.exposeAssets?.map((asset: AssetRef) => asset._id),
        exposeLabels: result.json.device.kioskConfig.exposeLabels?.map((asset: AssetRef) => asset._id),
        countLabelSeparately: result.json.device.kioskConfig?.exposeLabels?.[0]?.countLabel,
        alarm: result.json.device.kioskConfig?.alarm !== undefined ? {
          ...result.json.device.kioskConfig.alarm,
          audio: result.json.device.kioskConfig.alarm.focus?.red?.sound?.enabled || false,
          flash: result.json.device.kioskConfig.alarm.focus?.red?.flash || false,
          focus: result.json.device.kioskConfig.alarm.focus ? {
            ...result.json.device.kioskConfig.alarm.focus.red,
          } : undefined,
        } : undefined,
        homepage: {
          ...result.json.device.kioskConfig?.homepage,
          logo: result.json.device.kioskConfig?.homepage?.logo ? { title: 'image', src: result.json.device.kioskConfig.homepage.logo } : undefined,
        },
      };

    }
    if (resource === 'registration-templates') {

      result.json.profile = {
        ...result.json.profile,
        assetGroups: result.json.profile?.assetGroups?.map((assetGroup: AssetGroupRef) => assetGroup._id),
        configGroups: result.json.profile?.configGroups?.map((configGroup: ClientConfigGroupRef) => configGroup._id),
        assetGroupLimit: result.json.profile?.assetGroupLimit?.map((assetGroup: AssetGroupRef) => assetGroup._id),
      };

    }

    if (resource === 'report-triggers') {

      if (result.json.config?.type === 'zoneOverview' && result.json.config?.minOccupancy?.assetCount) {

        if (result.json.config.schedule?.entries?.length > 0) {

          result.json.config.schedule.entries.forEach((entry: any) => {

            entry.minOccupancy = entry.minOccupancy || result.json.config.minOccupancy.assetCount;

          });

        } else {

          result.json.config.schedule = {
            entries: [
              {
                daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
                timeStart: '00:00',
                timeEnd: '23:59',
                minOccupancy: result.json.config.minOccupancy.assetCount,
              },
            ],
          };

        }
        delete result.json.config.minOccupancy;

      }

      result.json.config = {
        ...result.json.config,
        filter: {
          ...result.json.config?.filter,
          assets: result.json.config?.filter?.assets?.map((asset: AssetRef) => asset?._id || asset),
          assetGroups: result.json.config?.filter?.assetGroups?.map((assetGroup: AssetGroupRef) => assetGroup?._id || assetGroup),
        },
        ...(result.json.config?.assets && { assets: result.json.config?.assets.map((asset: AssetRef) => asset._id) }),
        ...(result.json.config?.asset && { asset: result.json.config?.asset._id }),
        ...(result.json.config?.exclude && { exclude: result.json.config?.exclude.map((customer: CustomerRef) => customer._id) }),
        ...(result.json.config?.assetGroups && { assetGroups: result.json.config?.assetGroups.map((assetGroup: AssetGroupRef) => assetGroup._id) }),
      };

      result.json.channels = result.json.channels.map((channel: any) => {

        if (channel.type === 'email') {

          return {
            ...channel,
            address: channel.address || [],
            contactLists: channel.contactLists?.map((contactList: ContactListRef) => (contactList._id)),
          };

        } if (channel.type === 'sms') {

          return {
            ...channel,
            numbers: channel.numbers?.map((number: string) => {

              return { sms: number };

            }),
            contactLists: channel.contactLists?.map((contactList: ContactListRef) => (contactList._id)),
          };

        }
        return {
          ...channel,
        };

      });

    }

    if (resource === 'observation-triggers') {

      result.json = normalizeObservationTriggerForForm(result.json);

    }

    if (resource === 'domain-links') {

      result.json.customers = result.json.customers.map((customer: CustomerRef) => customer._id);

    }
    const record = RecordNameAsRaXgacRecord(result.json, resource);
    return { data: record };

  },

  getMany: async (resource, params) => {

    if (resource === 'client-config-groups') {

      const customer = params.meta?.customer || getCurrentCustomer();
      const records = await Promise.all(params.ids.map(async (id: Identifier) => {

        const response = await typedHttpClient().clientConfig.clientConfigControllerFindGroupById(
          String(id),
          customer?.value ? { customer: customer.value } : undefined,
        );
        return parseClientConfigGroupRecord(response.data);

      }));
      return { data: records };

    }

    let query: any;

    const mappedIds = params.ids.map((id: any) => {

      if (typeof id === 'string') {

        return id;

      }
      return id._id;

    });

    query = { _id: mappedIds };

    if (params.meta?.sort) {

      query = { ...query, $sort: params.meta.sort };

    }

    if (resource === 'customer-user-relations-by-user') {

      const currentCustomer = getCurrentCustomer();
      query = {
        'user._id': mappedIds.join(),
        'customer._id': currentCustomer?.value,
        ...(params.meta?.sort ? { $sort: params.meta.sort } : {}),
      };
      resource = 'customer-user-relations';

    }

    if (resource === 'app-users') {

      query = { ...query, _id: mappedIds, app: '!null' };

    }
    resource = preTranslateResource(resource);
    if (resource === 'responsePartners') {

      resource = 'customers';

    }
    if (resource === 'all-assets') {

      resource = 'assets';

    }
    const entitiesToReturn: any[] = [];
    const getEntities = async (offset: number) => {

      query._id = Array.isArray(query._id) ? query._id : [query._id];
      const ids = query._id.slice(offset, offset + 100);

      if (ids.length === 0) {

        return;

      }
      const max = ids.length > 100 ? 100 : ids.length;
      const slicedQuery = {
        ...query, _id: ids.join(','), $max: max,
      };

      const url = `${apiUrl}/${resource}?${queryString.stringify(slicedQuery)}`;

      const response = await httpClient(url);
      entitiesToReturn.push(...response.json.result);
      if (response.json.result.length > 0 && entitiesToReturn.length < params.ids.length) {

        await getEntities(offset + response.json.result.length);

      }

    };
    await getEntities(0);
    return {
      data: entitiesToReturn.map((record: Record<string, unknown>) => {

        const raRecord = RecordNameAsRaXgacRecord(record, resource) as RaXgacRecord<any>;
        if (resource === 'certification-categories') {

          raRecord.largestReminderPeriodInDays = calculateLargestReminderPeriodInDays(
            raRecord.reminderPeriods ?? [],
          );

        }
        if (resource === 'security-inventory-categories') {

          raRecord.largestReminderPeriodInDays = calculateLargestReminderPeriodInDays(
            raRecord.reminderPeriods ?? [],
          );

        }
        if (resource === 'assets') {

          raRecord.certificationCategories = _.uniq(
            (raRecord.certifications ?? [])
              .map((c: { certificationCategory: { _id: string } }) => c.certificationCategory._id),
          );

        }
        return raRecord;

      }),
    };

  },

  getManyReference: async (resource, params) => {

    resource = preTranslateResource(resource);
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      $sort: JSON.stringify([field, order]),
      $offset: (page - 1) * perPage,
      $max: perPage,
    };

    const url = `${apiUrl}/${resource}?${params.target}=${params.id}&${queryString.stringify(query)}`;

    const referenceRequest = await httpClient(url).then(({ headers, json }) => ({
      data: json.result.map((record: any) => {

        return {
          id: record._id,
          ...record,
          ...(resource === 'customer-user-relations' ? {
            customerId: record.customer._id,
            userId: record.user._id,
            assetGroupRestrictions: record.assetGroupRestrictions?.map((assetGroup: any) => assetGroup._id),
          } : {}),
        };

      }),
      total: parseInt(headers.get('content-range')?.split('/').pop() || '0', 10),
    }));

    return referenceRequest;

  },

  create: async (resource, params) => {

    if (resource === 'client-config-groups') {

      const payload = {
        name: params.data.name,
        clientConfig: params.data.clientConfig,
        customer: params?.data?.customer || null,
      };
      const response = await typedHttpClient().clientConfig.clientConfigControllerCreateGroup(payload);
      invalidateClientConfigGroupsListCaches();
      return { data: parseClientConfigGroupRecord(response.data) };

    }

    resource = preTranslateResource(resource);

    delete params.data.id;
    delete params.data._id;
    delete params.data.createdAt;
    if (!['customer-user-relations', 'zones', 'observation-triggers', 'report-triggers', 'contact-lists'].includes(resource)) {

      delete params.data.customer;

    }

    if (resource === 'static-responders' && (!params.data.integrations.sequrix.enabled || !params.data.integrations.sequrix.objectCode)) {

      delete params.data.integrations;

    }
    delete params.data.updatedAt;
    delete params.data.telluId;
    let image;

    if (resource === 'assets' || resource === 'users') {

      image = params.data.properties.image;
      params.data.properties.image = undefined;

    }
    if (resource === 'user-certifications') {

      const document = params.data.document;
      delete params.data.document;

      const assetId = params.meta?.assetId;
      const { json } = await httpClient(`${apiUrl}/assets/${assetId}/certifications`, {
        method: 'POST',
      }, params.data);

      if (document?.rawFile) {

        const formData = new FormData();
        formData.append('document', document.rawFile);

        // user can only have one certification per category, so can find the certification that was created by the category id
        const certificationId = json.certifications.find((c: any) => c.certificationCategory._id === params.data.certificationCategory._id)?._id;

        await httpClient(`${apiUrl}/assets/${assetId}/certifications/${certificationId}/document`, {
          method: 'POST',
          body: formData,
        });

      }

      return { data: { ...json, id: json._id } };

    }
    if (resource === 'overlays') {

      image = params.data.imageUrl;
      params.data.imageUrl = null;

    }

    if (resource === 'assets' && params.meta?.needsUserCreation) {

      if (!params.meta.code) {

        throw new Error('Registration code is required for user creation');

      }
      let userNameCheckError = null;
      await httpClient(`${apiUrl}/users/check-username`, {
        method: 'POST',
      }, params.data.username).catch((error) => {

        if (error.status === 409) {

          userNameCheckError = {
            data: {
              id: null,
              error: true,
              message: `Username ${params.data.username} already exists`,
            },
          };

        }

      });

      if (userNameCheckError) {

        return userNameCheckError;

      }

      const registerResult = await qrCodeDataProvider.registerUser(
        params.data.name,
        params.data.username,
        params.data.properties,
        params.data.externalId,
        params.data.available,
        false,
        params.meta.code,
      );

      if (registerResult && registerResult.status !== 200) {

        return {
          data: {
            id: null,
            error: true,
            message: registerResult.message,
          },
        };

      }
      const createdUserRequest = await httpClient(`${apiUrl}/users?${queryString.stringify({ username: params.data.username })}`);
      const createdUser = createdUserRequest.json.result[0];
      const createdAsset = await httpClient(`${apiUrl}/assets?${queryString.stringify({ 'user._id': createdUser._id })}`);
      if (params.data.isAdmin) {

        const currentCustomer = getCurrentCustomer();
        const customerUserRelationRequest = await httpClient(`${apiUrl}/customer-user-relations?${queryString.stringify({
          'customer._id': currentCustomer?.value, 'user._id': createdUser._id,
        })}`);
        const customerUserRelation = customerUserRelationRequest.json.result[0];
        if (customerUserRelation) {

          await xgacDataProvider.update('customer-user-relations', {
            id: customerUserRelation._id,
            data: {
              roles: [
                ...customerUserRelation.roles,
                'bhvk_admin',
              ],
            },
            previousData: customerUserRelation,
          });

        }

        await httpClient(`${apiUrl}/users/reset-password/${createdUser._id}?bhvk=true&admin_role_assigned=true`);

      }

      return {
        data: {
          id: createdAsset.json?.result[0]?._id || null,
          user: createdUser,
        },
      };

    }

    const currentCustomer = JSON.parse(sessionStorage.getItem('currentCustomer') || localStorage.getItem('lastSavedCustomer') || '{}');

    if (resource === 'users') {

      const roles = [...params.data.roles || []];
      delete params.data.roles;
      let userPostResult;
      if (_.isEqual(params.data.globalRoles, ['sales_global'])) {

        userPostResult = await httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
        }, { ...params.data });

      } else {

        try {

          userPostResult = await httpClient(`${apiUrl}/users/with-relation`, {
            method: 'POST',
          }, {
            user: params.data,
            relation: {
              customer: { _id: currentCustomer.value, _ref: 'Customer' },
              assetGroupRestrictions:
                  params.data.assetGroupRestrictions?.map((assetGroup: string): AssetGroupRef => ({ _id: assetGroup, _ref: 'AssetGroup' })) || [],
              roles,
            },
          });

        } catch (error: any) {

          if (error.status === 409) {

            throw new Error('Conflict');

          }
          throw error;

        }

      }

      if (image?.rawFile) {

        await sendPicture(image.rawFile, userPostResult.json._id, 'users');

      }

      return { data: { ...userPostResult.json, id: userPostResult.json._id } };

    }
    if (resource === 'customers') {

      const redOrangeTemplateId = ATS_CUSTOMER_LINK_RED_ORANGE_WITH_ALARMCENTER_TEMPLATE_ID;
      delete params.data.hasAlarmCenter;

      const customerCreateResult: { data: any } = await httpClient(`${apiUrl}/${resource}`, {
        method: 'POST',
      }, params.data).then(({ json }) => ({
        data: { ...params.data, id: json._id },
      }));

      if (!customerCreateResult?.data?.id) {

        throw new Error('Customer create failed');

      }
      const promises = [];

      promises.push(...customerCreateDefaults(customerCreateResult?.data?.id, redOrangeTemplateId, customerCreateResult?.data?.timezone));

      await Promise.allSettled(promises);

      return customerCreateResult;

    }

    if (resource === 'customer-user-relations') {

      const createResult = await httpClient(`${apiUrl}/${resource}`, {
        method: 'POST',
      }, {
        customer: { _id: params.data.customer._id, _ref: 'Customer' },
        user: { _id: params.data['user._id'], _ref: 'User' },
        assetGroupRestrictions: params.data.assetGroupRestrictions?.map((assetGroup: string): AssetGroupRef => ({ _id: assetGroup, _ref: 'AssetGroup' })),
        accessRequest: params.data.accessRequest?.validUntil ? {
          ...params.data.accessRequest,
          validUntil: new Date(params.data.accessRequest.validUntil).toISOString(),
          reason: params.data.accessRequest.reason || 'Access given by admin via Management Dashboard',
          review: {
            type: 'approved',
            reviewedBy: {
              _id: await getIdentity(),
              _ref: 'User',
            },
            forced: false,
            reviewedAt: new Date().toISOString(),
            reason: 'Access given by admin via Management Dashboard',
          },
        } : undefined,
        roles: params.data.roles,
      }).then(({ json }) => ({
        data: { ...params.data, id: json._id },
      }));

      return createResult;

    }

    // Only occurs when creation happens by means of cloning
    if (typeof params.data.customer === 'object') {

      params.data.customer = params.data.customer._id;

    }

    params.data = toXgacCreateDto(params.data, resource);
    const createResult = await httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
    }, {
      ...params.data,
      customer: ['domain-links'].includes(resource) ? undefined : { _id: params.data.customer || currentCustomer.value, _ref: 'Customer' },
    }).then(({ json }) => ({
      data: { ...json, id: json._id },
    }));

    if (image && (resource === 'assets' || resource === 'overlays')) {

      await sendPicture(image.rawFile, createResult.data.id, resource);

    }

    return createResult;

  },

  update: async (resource, params) => {

    if (resource === 'client-config-groups') {

      const response = await typedHttpClient().clientConfig.clientConfigControllerUpdateGroup(
        String(params.id),
        params.data,
      );
      invalidateClientConfigGroupsListCaches();
      return { data: parseClientConfigGroupRecord(response.data) };

    }

    delete params.data.hasAlarmCenter;
    const id = params.data._id || params.data.id;

    params.data = updateComparison(params.data, params.previousData);

    if (resource === 'static-responders' && (params.data?.integrations || params.previousData?.integrations)) {

      params.data.integrations = {
        sequrix: {
          enabled: params.data?.integrations?.sequrix?.enabled || false,
          objectCode: params.data?.integrations?.sequrix?.objectCode || params.previousData?.integrations?.sequrix?.objectCode || ' ',
        },
      };

    }

    // This line is added to prevent empty object to be sent to the backend.
    // This can occur when multiple resources can be edited on one page, and not all contain changes.
    if (_.isEqual(params.data, {})) {

      return { data: params.previousData };

    }

    resource = preTranslateResource(resource);
    if (resource !== 'customer-user-relations' && resource !== 'customer-alarm-center-relations') {

      delete params.data.customer;

    } else {

      delete params.data.user;

    }

    if (resource === 'overlays') {

      if (params.data.imageUrl?.rawFile) {

        const imageUrl = await sendPicture(params.data.imageUrl.rawFile, id, resource);
        params.data.imageUrl = imageUrl;

      } else {

        params.data.imageUrl = params.data.imageUrl.src;

      }

    }
    if (resource === 'specials/kiosk-user') {

      if (params.data.device?.kioskConfig?.homepage?.logo) {

        if (params.data.device.kioskConfig.homepage.logo.rawFile) {

          const imageUrl = await sendPicture(params.data.device.kioskConfig.homepage.logo.rawFile, params.data.asset._id);
          params.data.device.kioskConfig.homepage.logo = imageUrl;

        } else {

          params.data.device.kioskConfig.homepage.logo = params.data.device.kioskConfig.homepage.logo.src;

        }

      }

    }

    if (resource === 'customers') {

      if (params.data.alarmCenter?.callbackPhoneNumber === null || params.data.alarmCenter?.callbackPhoneNumber === '') {

        delete params.data.alarmCenter.callbackPhoneNumber;

      }

      if (params.data.kioskConfig?.homepage?.logo) {

        if (params.data.kioskConfig.homepage.logo.rawFile) {

          const imageUrl = await sendPicture(params.data.kioskConfig.homepage.logo.rawFile, id);
          params.data.kioskConfig.homepage.logo = imageUrl;

        } else {

          params.data.kioskConfig.homepage.logo = params.data.kioskConfig.homepage.logo.src;

        }

      }

    }
    if (resource === 'report-triggers') {

      if (params.data.config && !params.data.config.type) {

        params.data.config.type = params.previousData.config.type;

      }

    }

    if (resource === 'assets' || resource === 'users' || resource === 'app-users') {

      if (params.data.properties?.image) {

        if (params.data.properties?.image?.rawFile) {

          const imageUrl = await sendPicture(params.data.properties.image.rawFile, id, resource);
          params.data.properties.image = imageUrl;

        } else {

          params.data.properties.image = params.data.properties.image.src;

        }

      }

    }
    if (resource === 'user-certifications') {

      const assetId = params.meta?.assetId;
      const certificationId = params.meta?.certificationId;
      const document = params.data.document;
      delete params.data.document;

      // initial check is not working, as the document is included in the params.data object.
      // therefore params.data is never equal to an empty object until the document is removed.
      let json: Asset | null = null;
      if (!_.isEqual(params.data, {})) {

        json = await httpClient(`${apiUrl}/assets/${assetId}/certifications/${certificationId}`, {
          method: 'PATCH',
        }, params.data).then((data) => data.json);

      }

      if (document?.rawFile) {

        const formData = new FormData();
        formData.append('document', document.rawFile);

        await httpClient(`${apiUrl}/assets/${assetId}/certifications/${certificationId}/document`, {
          method: 'POST',
          body: formData,
        });

      }

      return json ? { data: { ...json, id: json._id } } : { data: params.previousData };

    }
    if (resource === 'users') {

      if (params.data.username === params.previousData?.username) {

        delete params.data.username;

      }

    }
    if (resource === 'customer-user-relations') {

      params.data = {
        ...(params.data.customer?._id ? { customer: { _id: params.data.customer._id, _ref: 'Customer' } } : {}),
        ...(params.data['user._id'] ? { user: { _id: params.data['user._id'], _ref: 'User' } } : {}),
        ...(params.data.assetGroupRestrictions
          ? {
            assetGroupRestrictions: params.data.assetGroupRestrictions?.map((assetGroup: string): AssetGroupRef => (
              { _id: assetGroup, _ref: 'AssetGroup' }
            )),
          }
          : {}),
        accessRequest: params.data.accessRequest?.validUntil ? {
          ...params.data.accessRequest,
          validUntil: new Date(params.data.accessRequest.validUntil).toISOString(),
          reason: params.data.accessRequest.reason || 'Access given by admin via Management Dashboard',
          review: {
            type: 'approved',
            reviewedBy: {
              _id: await getIdentity(),
              _ref: 'User',
            },
            forced: false,
            reviewedAt: new Date().toISOString(),
            reason: 'Access given by admin via Management Dashboard',
          },
        } : undefined,
        roles: params.data.roles,
      };

    }

    params.data = toXgacUpdateDto(params.data, resource);

    const result = httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PATCH',
    }, { ...params.data }).then(({ json }) => ({ data: { ...json, id: json._id } })).catch((error) => {

      if (resource === 'users' && error.status === 409) {

        throw new Error('Conflict');

      }
      throw error;

    });

    return result;

  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  updateMany: (resource, params) => {

    const updatePromises = [];
    params.data = toXgacUpdateDto(params.data, resource);
    for (const id of params.ids) {

      updatePromises.push(httpClient(`${apiUrl}/${resource}/${id}`, {
        method: 'PATCH',
      }, params.data).then(({ json }) => ({ data: json })));

    }

    return Promise.allSettled(updatePromises);

  },

  delete: (resource, params) => {

    if (resource === 'client-config-groups') {

      return typedHttpClient().clientConfig.clientConfigControllerRemoveGroup(String(params.id)).then(() => {

        invalidateClientConfigGroupsListCaches();
        return { data: { id: params.id } };

      });

    }

    if (resource === 'app-users') {

      return httpClient(`${apiUrl}/assets/app-user`, {
        method: 'DELETE',
      }, { assetId: params.id, deleteAllDevices: params.meta?.deleteAllDevices || false }).then(({ json }) => ({ data: json }));

    }

    if (resource === 'user-certifications') {

      const assetId = params.meta?.assetId;

      return httpClient(`${apiUrl}/assets/${assetId}/certifications/${params.id}`, {
        method: 'DELETE',
      }).then(({ json }) => ({ data: { ...json, id: params.id } }));

    }

    resource = preTranslateResource(resource);
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: json }));

  },

  deleteMany: async (resource, params): Promise<any> => {

    const resourceBeforeTranslate = resource;
    if (resource !== 'app-users') {

      resource = preTranslateResource(resource);

    }
    const deletedIds: Identifier[] = [];
    for (const currentId of params.ids) {

      try {

        if (resourceBeforeTranslate === 'client-config-groups') {

          await typedHttpClient().clientConfig.clientConfigControllerRemoveGroup(String(currentId));

        } else if (resource === 'app-users') {

          await httpClient(`${apiUrl}/assets/app-user`, {
            method: 'DELETE',
          }, { assetId: currentId, deleteAllDevices: params.meta.deleteAllDevices || false }).then(({ json }) => ({ data: json }));

        } else {

          await httpClient(`${apiUrl}/${resource}/${currentId}`, {
            method: 'DELETE',
          });

        }
        deletedIds.push(currentId);

      } catch (e) {

        console.log({ message: `failed to delete ${currentId}`, e });

      }

    }

    if (resourceBeforeTranslate === 'client-config-groups' && deletedIds.length > 0) {

      invalidateClientConfigGroupsListCaches();

    }

    return { data: deletedIds };

  },

  subscribe: (topic: string, subscriptionCallback: SubscriptionCallback) => {

    socketService.subscribe(topic, subscriptionCallback);

    return Promise.resolve({ data: null });

  },

  unsubscribe: (topic: string) => {

    socketService.unsubscribe(topic);
    return Promise.resolve({ data: null });

  },

  getAlarmCenters: async (id:string) => {

    const relationRequest = await httpClient(`${apiUrl}/customer-alarm-center-relations/populated/?customer._id=${id}`);
    return relationRequest.json.result.map((relation: { customer: Customer; alarmCenter: Customer }) => {

      return relation.alarmCenter.name;

    });

  },

  moveAppUsers: async (assetIds: string[], newCustomer: string, oldCustomerId: string) => {

    const postData = {

      ids: assetIds,
      fromCustomer: { _id: oldCustomerId, _ref: 'Customer' },
      toCustomer: { _id: newCustomer, _ref: 'Customer' },

    };

    const updateResults = await httpClient(`${apiUrl}/assets/move`, {
      method: 'POST',
    }, postData);
    const statuses = updateResults.json.map((result: any) => {

      if (!result || result.status === 'rejected') return 400;

      return 200;

    });

    return statuses;

  },
  getRolesForUser: async (id: string) => {

    const customer = JSON.parse(sessionStorage.getItem('currentCustomer') || localStorage.getItem('lastSavedCustomer') || '');

    return httpClient(`${apiUrl}/customer-user-relations?${queryString.stringify({ 'user._id': id, 'customer._id': customer.value })}`).then(({ json }) => (
      { data: json.result[0].roles }));

  },

  getAppUsers: async (customerIds: Array<string>) => {

    const query = {
      'customer._id': customerIds.join(),
      app: '!null',
    };
    return httpClient(`${apiUrl}/assets?${queryString.stringify(query)}`, {
      method: 'GET',
    });

  },

  syncTree: async () => {

    await httpClient(`${apiUrl}/customers/62908a4b-0d8d-4787-975b-a854205aa99f`, { method: 'PATCH' }, { name: '06.1 Development' });

  },

  getBeaconsForZone: async (zoneId:string) => {

    const beaconRequest = await httpClient(`${apiUrl}/beacons?${queryString.stringify({ 'zone._id': zoneId })}`, { method: 'GET' });
    if (!beaconRequest.json) {

      return { total: 0 };

    }
    return beaconRequest.json || { total: 0 };

  },

  massAcknowledge: async (comment: string, ids?: string[]) => {

    return httpClient(`${apiUrl}/alarms/mass-acknowledge`, { method: 'POST' }, {
      customer: { _id: getCurrentCustomer()?.value, _ref: 'Customer' },
      comment,
      ...(ids ? { alarmIds: ids } : {}),
    });

  },

  updateAssetsInGroup: async (payload: any) => {

    return httpClient(`${apiUrl}/asset-groups/mass-update`, { method: 'POST' }, payload);

  },

  getAll: async (resource: string, params: {
    sort: SortPayload;
    filter: any;
    meta?: any; }) => {

    let total = params.meta?.total || null;
    const resultData = [];
    let page = 1;
    let tries = 3;
    while ((total === null || resultData.length < total) && tries > 0) {

      const newFilter = { ...params.filter };
      let hasAlteredFilter = false;

      // eslint-disable-next-line @typescript-eslint/no-loop-func
      Object.keys(newFilter).forEach((key) => {

        if (Array.isArray(newFilter[key]) && newFilter[key].length > 100) {

          newFilter[key] = newFilter[key].slice((page - 1) * 100, page * 100);
          hasAlteredFilter = true;

        }

      });

      const iterationResult = await xgacDataProvider.getList(
        resource,
        { ...params, filter: newFilter, pagination: { page: hasAlteredFilter ? 1 : page, perPage: 100 } },
      );
      if (total === null) total = iterationResult.total || null;
      if (iterationResult.data.length === 0 && tries > 0) {

        tries--;
        continue;

      } else if (iterationResult.data.length > 0) {

        tries = 3;

      }
      resultData.push(...iterationResult.data);
      page++;

    }

    return { data: resultData, total };

  },

  massMoveDevices: async (selectedIds: Identifier[], newCenterPosition: PointGeometry) => {

    const circlePositions = selectedIds.length > 1 ? getPointsAroundPosition(selectedIds.length, newCenterPosition) : [newCenterPosition.coordinates];
    const updatePromises = [];
    for (let i = 0; i < selectedIds.length; i++) {

      updatePromises.push(xgacDataProvider.update('assets', {
        previousData: undefined,
        id: selectedIds[i],
        data: {
          position: { ...newCenterPosition, coordinates: circlePositions[i] },
        },
      }));

    }
    const promiseResults = await Promise.allSettled(updatePromises);
    if (promiseResults.some((result) => result.status === 'rejected')) {

      throw new Error('Failed to move devices');

    }
    return { data: null };

  },

  getExpiringCertificationsCountPerCustomer: async (customerId: string) => {

    return httpClient(`${apiUrl}/assets/count-of-certifications-considered-expiring/${customerId}`);

  },

};
